<template>
  <section class="emotional-stability">
    <div class="emotional-stability__header">
      <h2 class="emotional-stability__title">
        Эмоциональная устойчивость и мотивация
      </h2>

      <div class="emotional-stability__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            :disabled="!hasSubscription"
            class="emotional-stability__select"
        />

        <div>
          <a-range-picker
              v-model:value="selectedPeriod"
              :disabled="!hasSubscription"
              :placeholder="['Дата начала', 'Дата конца']"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              separator="-"
              inputReadOnly
              class="emotional-stability__date compact-range-picker"
          />
        </div>
      </div>
    </div>

    <div class="emotional-stability__grid">
      <SmallCardWrapper
          name="Индекс устойчивости и мотивации"
          :helpText="'Индекс устойчивости и мотивации'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <StabilityMotivationIndexCard
            :items="stabilityItems"
            :chartLabels="stabilityChart.labels"
            :chartDatasets="stabilityChart.datasets"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Индекс хронической усталости"
          :helpText="'Индекс хронической усталости'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <ChronicFatigueIndexCard
            :levels="fatigueLevels"
            :positions="fatiguePositions"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'

import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import StabilityMotivationIndexCard from '@/components/Analytics/StabilityMotivationIndexCard.vue'
import ChronicFatigueIndexCard from '@/components/Analytics/ChronicFatigueIndexCard.vue'
import SubscriptionRequiredWarning from '@/components/OtherElements/SubscriptionRequiredWarning.vue'

export default {
  name: 'EmotionalStability',

  components: {
    SubscriptionRequiredWarning,
    ChronicFatigueIndexCard,
    StabilityMotivationIndexCard,
    SmallCardWrapper,
  },

  setup() {
    const store = useStore();
    const groups = computed(() => store.state.group.groups);

    const selectedGroup = ref('all');
    const selectedPeriod = ref(null);
    const dataIsLoading = ref(false);

    const hasSubscription = ref(true);

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    })

    const emotionalStabilityDynamicsData = computed(() => {
      return store.state.analytics.monitoringData?.emotionalStabilityDynamics ?? null
    });

    const DEFAULT_STABILITY_ITEMS = [
      {
        key: 'resilience',
        label: 'Хроническая усталость',
        change: 6.1,
        value: 8.2,
        color: '#5C7CFA',
      },
      {
        key: 'stress',
        label: 'Индекс стресса',
        change: 0,
        value: 2,
        color: '#46C7D8',
      },
      {
        key: 'monotony',
        label: 'Индекс монотонии',
        change: -4,
        value: -1,
        color: '#E333C8',
      },
      {
        key: 'motivation',
        label: 'Снижение мотивации',
        change: 3.4,
        value: 5,
        color: '#B45CFF',
        warning: 'Снижененная мотивация'
      },
    ]

    const DEFAULT_STABILITY_CHART = {
      labels: ['II кв.', 'III кв.', 'IV кв.', 'I кв.'],
      datasets: [
        {
          key: 'resilience',
          label: 'Хроническая усталость',
          values: [-2, 3, -1, -0.5],
          color: '#5C7CFA',
        },
        {
          key: 'stress',
          label: 'Индекс стресса',
          values: [1, 9.5, 0.5, 3],
          color: '#46C7D8',
        },
        {
          key: 'monotony',
          label: 'Индекс монотонии',
          values: [-9, 2, -5.5, -3],
          color: '#E333C8',
        },
        {
          key: 'motivation',
          label: 'Снижение мотивации',
          values: [7, 6, 9, 7],
          color: '#B45CFF',
        },
      ],
    }

    const DEFAULT_FATIGUE_LEVELS = [
      {
        key: 'low',
        label: 'Низкий уровень',
        change: 7,
        value: 6,
        color: '#21AC0C',
      },
      {
        key: 'moderate',
        label: 'Умеренный',
        change: 10,
        value: 24,
        color: '#99DA6E',
      },
      {
        key: 'high',
        label: 'Высокий',
        change: 15,
        value: 42,
        color: '#FF9F40',
      },
      {
        key: 'critical',
        label: 'Критический',
        change: 3,
        value: 12,
        color: '#F55565',
      },
    ]

    const DEFAULT_FATIGUE_POSITIONS = [
      {
        name: 'Линейный персонал',
        value: 3,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Специалисты',
        value: 8,
        max: 10,
        color: '#FF9F40',
      },
      {
        name: 'Старшие специалисты',
        value: 5,
        max: 10,
        color: '#99DA6E',
      },
      {
        name: 'Руководители',
        value: 1,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Топ-менеджмент',
        value: 10,
        max: 10,
        color: '#F55565',
      },
    ]

    const stabilityItems = ref([])
    const stabilityChart = ref({
      labels: [],
      datasets: [],
    })
    const fatigueLevels = ref([])
    const fatiguePositions = ref([])

    const getSafeNumber = (value) => {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : 0
    }

    const getSignedChange = (score, previous, trend) => {
      const diff = Math.abs(getSafeNumber(score) - getSafeNumber(previous))

      if (trend === 'down') {
        return -diff
      }

      if (trend === 'up') {
        return diff
      }

      return getSafeNumber(score) - getSafeNumber(previous)
    }

    const getFatigueLevelColor = (title) => {
      const normalizedTitle = String(title).toLowerCase()

      if (normalizedTitle.includes('низ')) {
        return '#21AC0C'
      }

      if (normalizedTitle.includes('умер')) {
        return '#99DA6E'
      }

      if (normalizedTitle.includes('высок')) {
        return '#FF9F40'
      }

      if (normalizedTitle.includes('крит')) {
        return '#F55565'
      }

      return '#D9D9D9'
    }

    const periodInterval = computed(() => {
      if (
          !Array.isArray(selectedPeriod.value) ||
          selectedPeriod.value.length !== 2 ||
          !selectedPeriod.value[0] ||
          !selectedPeriod.value[1]
      ) {
        return null
      }

      const [from, to] = selectedPeriod.value

      return [
        dayjs(from).format('DD.MM.YYYY'),
        dayjs(to).format('DD.MM.YYYY'),
      ].join('-')
    })

    const buildStabilityFromBackend = (data) => {
      const resilience = data?.resilience_motivation_index || {}

      const items = [
        {
          key: 'resilience',
          label: 'Хроническая усталость',
          change: getSignedChange(resilience?.score, resilience?.previous, resilience?.trend),
          value: getSafeNumber(resilience?.score),
          color: '#597EF7',
        },
        {
          key: 'stress',
          label: 'Индекс стресса',
          change: getSignedChange(
              resilience?.stress_index?.score,
              resilience?.stress_index?.previous,
              resilience?.stress_index?.trend
          ),
          value: getSafeNumber(resilience?.stress_index?.score),
          color: '#2AC8E0',
        },
        {
          key: 'monotony',
          label: 'Индекс монотонии',
          change: getSignedChange(
              resilience?.monotony_index?.score,
              resilience?.monotony_index?.previous,
              resilience?.monotony_index?.trend
          ),
          value: getSafeNumber(resilience?.monotony_index?.score),
          color: '#F323C6',
        },
        {
          key: 'motivation',
          label: 'Снижение мотивации',
          change: getSignedChange(
              resilience?.decreased_motivation?.score,
              resilience?.decreased_motivation?.previous,
              resilience?.decreased_motivation?.trend
          ),
          value: getSafeNumber(resilience?.decreased_motivation?.score),
          color: '#AE3FF3',
        },
      ]

      return {
        items,
        chart: {
          labels: [],
          datasets: [
            {
              key: 'resilience',
              label: 'Хроническая усталость',
              values: [getSafeNumber(resilience?.score)],
              color: '#597EF7',
            },
            {
              key: 'stress',
              label: 'Индекс стресса',
              values: [getSafeNumber(resilience?.stress_index?.score)],
              color: '#2AC8E0',
            },
            {
              key: 'monotony',
              label: 'Индекс монотонии',
              values: [getSafeNumber(resilience?.monotony_index?.score)],
              color: '#F323C6',
            },
            {
              key: 'motivation',
              label: 'Снижение мотивации',
              values: [getSafeNumber(resilience?.decreased_motivation?.score)],
              color: '#AE3FF3',
            },
          ],
        },
      }
    }

    const buildFatigueFromBackend = (data) => {
      const fatigue = data?.chronic_fatigue_index || {}

      const levels = Array.isArray(fatigue?.value)
          ? fatigue.value.map((item, index) => ({
            key: `level_${index}`,
            label: item?.title || '',
            change: getSignedChange(item?.score, item?.previous, item?.trend),
            value: getSafeNumber(item?.score),
            color: getFatigueLevelColor(item?.title),
          }))
          : []

      const positions = Array.isArray(fatigue?.by_position)
          ? fatigue.by_position.map((item) => ({
            name: item?.title || '',
            value: getSafeNumber(item?.score),
            max: 10,
            color: getFatigueLevelColor(item?.title),
          }))
          : []

      return {
        levels,
        positions,
      }
    }

    const fillCardsData = (backendData) => {
      if (!hasSubscription.value) {
        stabilityItems.value = DEFAULT_STABILITY_ITEMS.map((item) => ({ ...item }))
        stabilityChart.value = {
          labels: [...DEFAULT_STABILITY_CHART.labels],
          datasets: DEFAULT_STABILITY_CHART.datasets.map((item) => ({
            ...item,
            values: [...item.values],
          })),
        }
        fatigueLevels.value = DEFAULT_FATIGUE_LEVELS.map((item) => ({ ...item }))
        fatiguePositions.value = DEFAULT_FATIGUE_POSITIONS.map((item) => ({ ...item }))
        return
      }

      const mappedStability = buildStabilityFromBackend(backendData)
      const mappedFatigue = buildFatigueFromBackend(backendData)

      stabilityItems.value = mappedStability.items
      stabilityChart.value = mappedStability.chart
      fatigueLevels.value = mappedFatigue.levels
      fatiguePositions.value = mappedFatigue.positions
    }

    const fetchData = () => {
      if (!hasSubscription.value) {
        fillCardsData(null)
        return
      }

      dataIsLoading.value = true

      store.dispatch('analytics/getManagementLevels');

      store.dispatch('analytics/getEmotionalStabilityDynamics', {
        group_id: selectedGroup.value === 'all' ? null : selectedGroup.value,
        date: periodInterval.value,
      }).then(() => {
        fillCardsData(emotionalStabilityDynamicsData.value)
      }).finally(() => {
        dataIsLoading.value = false
      })
    }

    onBeforeMount(() => {
      selectedPeriod.value = [
        dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
      ]

      fetchData();
    })

    watch(
        () => [selectedGroup.value, periodInterval.value, hasSubscription.value],
        () => {
          fetchData()
        }
    )

    return {
      selectedGroup,
      selectedPeriod,
      groupOptions,
      dataIsLoading,
      hasSubscription,
      stabilityItems,
      stabilityChart,
      fatigueLevels,
      fatiguePositions,
    }
  },
}
</script>

<style scoped lang="scss">
.emotional-stability {
  width: 100%;
  text-align: start;
}

.emotional-stability__header {
  margin-bottom: 20px;
}

.emotional-stability__title {
  margin: 0 0 16px;
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.emotional-stability__filters {
  display: flex;
  align-items: center;
  gap: 14px;
}

.emotional-stability__select {
  width: 350px;
}

.emotional-stability__date {
  width: 200px;
}

.emotional-stability__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

.emotional-stability__grid :deep(.ant-card-body) {
  padding: 0;
}

.emotional-stability__grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

@media (max-width: $tablet-breakpoint) {
  .emotional-stability__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .emotional-stability__filters {
    flex-direction: column;
    align-items: stretch;
  }

  .emotional-stability__select,
  .emotional-stability__date {
    width: 100%;
  }
}

.compact-range-picker {
  display: flex;
  align-items: center;
}

:deep(.compact-range-picker .ant-picker-input) {
  flex: 0 1 auto;
}

:deep(.compact-range-picker .ant-picker-input > input) {
  padding: 0;
  font-size: 14px;
  text-align: center;
}

:deep(.compact-range-picker .ant-picker-range-separator) {
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
}

:deep(.compact-range-picker .ant-picker-active-bar) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-clear) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-suffix) {
  margin-left: 6px;
  color: rgba(0, 0, 0, 0.45);
}

:deep(.compact-range-picker .ant-picker-input-placeholder > input) {
  text-align: center;
}

.locked {
  opacity: 0.5;
}

.subsription-locked {
  margin-bottom: 16px;
}
</style>